import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { StripeFieldName } from 'root-constants'
import { useDispatch, useSelector } from 'react-redux'
import { resetErrorAction } from 'root-redux/actions/common'
import { useLocation } from 'react-router'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { getPageIdFromPathName } from 'helpers/getPageIdFromPathName'
import { TCreditCardField, TPaymentErrorState } from 'modules/purchase/types'
import { select3DSecureIframeUrl } from 'modules/purchase/redux/selects'
import {
  getDefaultPaymentErrorsState,
  getPaymentErrorStateBySubmitWithUntouchedFields,
} from 'modules/purchase/helpers/rootHelpers'
import {
  EMPTY_FIELD_ERROR,
  THREEDS_REDIRECT_SEARCH_PARAM,
} from 'modules/purchase/constants'
import { check3DSecure, purchaseAction } from 'modules/purchase/redux/actions'
import {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js'
import {
  stripeElementStyle,
  StyledCreditCardForm as S,
} from './CreditCardForm.styles'

export const CreditCardForm: React.FC = () => {
  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const cardNumberElemRef = useRef<StripeCardNumberElement | null>(null)
  const cardExpiryElemRef = useRef<StripeCardExpiryElement | null>(null)
  const cvcElemRef = useRef<StripeCardCvcElement | null>(null)
  const cardholderNameElemRef = useRef<HTMLInputElement>(null)
  const threeDSecureIframeUrl = useSelector(select3DSecureIframeUrl)
  const [errors, setErrors] = useState<TPaymentErrorState>(() =>
    getDefaultPaymentErrorsState(),
  )
  const [name, setName] = useState('')
  const hasErrors = useMemo(
    () => Object.values(errors).some((error) => error.isShown && error.error),
    [errors],
  )
  const hasUntouchedFields = useMemo(
    () =>
      Object.values(errors).some((error) => error.isShown && !error.isTouched),
    [errors],
  )
  const hasUncompletedFields = useMemo(
    () => Object.values(errors).some((field) => !field.isComplete),
    [errors],
  )
  const isFormValid = useMemo(
    () => !hasErrors && !hasUntouchedFields && !hasUncompletedFields,
    [hasErrors, hasUncompletedFields, hasUntouchedFields],
  )

  useEffect(() => {
    const URLParams = new URLSearchParams(search)
    const isSuccess = URLParams.has(THREEDS_REDIRECT_SEARCH_PARAM)

    if (!isSuccess || !threeDSecureIframeUrl || !stripe) return

    dispatch(check3DSecure(stripe))
  }, [dispatch, search, stripe, threeDSecureIframeUrl])

  const handleChange = useCallback(
    ({
      fieldName,
      isEmpty,
      hasError,
      isComplete,
      nextElemRef,
    }: {
      fieldName: StripeFieldName
      isEmpty: boolean
      hasError: boolean
      isComplete: boolean
      nextElemRef?: RefObject<TCreditCardField>
    }) => {
      dispatch(resetErrorAction())

      let error = ''

      if (hasError) {
        error = 'is invalid'
      }

      if (isEmpty) {
        error = EMPTY_FIELD_ERROR
      }

      if (nextElemRef && isComplete) {
        nextElemRef.current?.focus()
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: { isTouched: true, error, isComplete },
      }))
    },
    [dispatch],
  )

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      dispatch(resetErrorAction())

      if (hasUntouchedFields) {
        setErrors(getPaymentErrorStateBySubmitWithUntouchedFields(errors))
        return
      }

      if (hasErrors) return

      const card = elements?.getElement(CardNumberElement)
      const currentPageId = getPageIdFromPathName(pathname)

      if (!stripe || !card || !currentPageId) return

      dispatch(
        purchaseAction({
          stripe,
          card,
          name,
          paymentPageId: currentPageId,
        }),
      )
    },
    [
      dispatch,
      hasUntouchedFields,
      hasErrors,
      elements,
      stripe,
      name,
      pathname,
      errors,
    ],
  )

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <S.InputContainer>
          <S.Label>{t`purchase.checkout.cardNumber`}</S.Label>
          <S.CardNumberElement
            onReady={(elem) => {
              elem.focus()
              cardNumberElemRef.current = elem
            }}
            options={{
              showIcon: false,
              placeholder: '1234 1234 1234 1234',
              style: stripeElementStyle,
            }}
            onChange={({ empty, error, complete }) => {
              handleChange({
                fieldName: StripeFieldName.NUMBER,
                isEmpty: empty,
                hasError: !!error,
                isComplete: complete,
                nextElemRef: cardExpiryElemRef,
              })
            }}
          />
          <S.CvvExpiryInputContainer>
            <S.CardExpiryContainer>
              <S.Label>{t`purchase.checkout.cardExpiry`}</S.Label>
              <S.CardExpiryElement
                onReady={(elem) => {
                  cardExpiryElemRef.current = elem
                }}
                options={{
                  placeholder: 'MM/YY',
                  style: stripeElementStyle,
                }}
                onChange={({ empty, error, complete }) => {
                  handleChange({
                    fieldName: StripeFieldName.EXPIRY,
                    isEmpty: empty,
                    hasError: !!error,
                    isComplete: complete,
                    nextElemRef: cvcElemRef,
                  })
                }}
              />
            </S.CardExpiryContainer>

            <S.CardCvcContainer>
              <S.Label>{t`purchase.checkout.securityNumber`}</S.Label>
              <S.CardCvcElement
                onReady={(elem) => {
                  cvcElemRef.current = elem
                }}
                options={{
                  placeholder: '***',
                  style: stripeElementStyle,
                }}
                onChange={({ empty, error, complete }) => {
                  handleChange({
                    fieldName: StripeFieldName.CVC,
                    isEmpty: empty,
                    hasError: !!error,
                    isComplete: complete,
                    nextElemRef: cardholderNameElemRef,
                  })
                }}
              />
            </S.CardCvcContainer>
          </S.CvvExpiryInputContainer>
          <S.Label>{t`purchase.checkout.cardholderName`}</S.Label>
          <S.CardholderInput
            ref={cardholderNameElemRef}
            type="text"
            placeholder={t`purchase.checkout.cardholderNamePlaceholder`}
            data-invalid={!!errors[StripeFieldName.NAME].error}
            onChange={(e) => {
              const value = e.target.value.trim()

              setName(value)

              handleChange({
                fieldName: StripeFieldName.NAME,
                isEmpty: false,
                hasError: false,
                isComplete: true,
              })
            }}
          />
        </S.InputContainer>
        <S.Button
          type="submit"
          disabled={!stripe || !isFormValid}
          disabledBackgroundColor="#898B95"
        >
          {t('purchase.checkout.confirmPayment')}
        </S.Button>
      </form>
    </div>
  )
}
